'use client';

import React, { useCallback } from 'react';
import { Tooltip } from '../Tooltip';
import { twMerge } from 'tailwind-merge';
interface PulseRadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  sx?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg'; // size customized so it was omitted from the props
  color?: 'primary' | 'secondary' | 'neutral' | 'soft';
  variant?: 'solid' | 'outlined';
  disabledReason?: string;
  'data-testid'?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const PulseRadio: React.FC<PulseRadioProps> = ({
  label,
  checked = false,
  sx,
  className,
  size = 'md',
  color = 'primary',
  variant = 'outlined',
  disabled = false,
  disabledReason,
  'data-testid': dataTestId,
  onChange,
  ...props
}) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
    },
    [onChange]
  );

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const colorClasses = {
    primary: {
      solid: 'border-blue-500 bg-blue-500',
      outlined: 'border-blue-500',
    },
    secondary: {
      solid: 'border-neutral-500 bg-neutral-500',
      outlined: 'border-neutral-500',
    },
    neutral: {
      solid: 'border-neutral-300 bg-neutral-300',
      outlined: 'border-neutral-300',
    },
    soft: {
      solid: 'border-neutral-200 bg-neutral-200',
      outlined: 'border-neutral-200',
    },
  };

  const radioClasses = twMerge(
    'border rounded-full relative',
    sizeClasses[size],
    checked ? colorClasses[color][variant] : 'border-neutral-300',
    checked &&
      variant === 'solid' &&
      'after:absolute after:w-2 after:h-2 after:bg-white after:rounded-full after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2',
    checked &&
      variant === 'outlined' &&
      'after:absolute after:w-2 after:h-2 after:bg-blue-500 after:rounded-full after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2',
    className
  );

  const radio = (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        className="hidden"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        style={{ ...sx }}
        aria-label={label || undefined}
        data-testid={dataTestId}
        {...props}
      />
      <span className={radioClasses} aria-hidden="true" />
      {label && (
        <span
          className={twMerge(
            'text-sm',
            size === 'lg' && 'text-base',
            size === 'sm' && 'text-xs'
          )}
        >
          {label}
        </span>
      )}
    </label>
  );

  return disabled && disabledReason ? (
    <Tooltip content={disabledReason}>{radio}</Tooltip>
  ) : (
    radio
  );
};

export default PulseRadio;
